/** @format */

import { axiosInstance } from '@common/application/context/Providers';
import convertFileToB64 from '@common/application/helpers/convertFileToB64';
import { Document } from '@common/model/apiClient/Document';
import { ApiResponse } from '@common/types/api';
import { ElasticResponse, ElasticSort } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';

export class Branch {
  id: number;
  companyId: number;
  name: string;
  addressLines: Array<string>;
  locality: string;
  region: string;
  postcode: string;
  countryId: string;
  erpId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;

  constructor(companyId: number, name: string, addressLines: Array<string>, locality: string, region: string, postcode: string, countryId: string, erpId: string) {
    this.companyId = companyId;
    this.name = name;
    this.addressLines = addressLines;
    this.locality = locality;
    this.region = region;
    this.postcode = postcode;
    this.countryId = countryId;
    this.erpId = erpId;
  }

  static list(
    query,
    sort,
    queryOpts?: Record<string, unknown>,
    size?: number,
    pit?: Nullable<string>,
    search_after?: Nullable<ElasticSort>,
    signal?: AbortSignal,
  ): ElasticResponse<Branch> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/branches`, {
          signal,
          params: {
            sequence_id: 1,
            pit,
            query,
            queryOpts: JSON.stringify(queryOpts ?? { deleted: false }),
            sort: JSON.stringify(sort),
            search_after: JSON.stringify(search_after),
            size,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static get(id: number): ApiResponse<Branch> {
    return axiosInstance.get(`/api/v1/branches/${id}`).then((res) => ({ ...res.data }));
  }

  static getStats(
    id: number,
    timeSpan: { start: Date; end: Date },
  ): Promise<{ message: string; paginated: boolean; payload: { savingsTotal: number; docCount: number; recentDocs: Array<Document> } }> {
    return axiosInstance.get(`/api/v1/branches/${id}/stats`, { params: { start: timeSpan.start, end: timeSpan.end } }).then((res) => ({ ...res.data }));
  }

  static delete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/branches/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/branches/${id}/undelete`).then((res) => ({ ...res.data }));
  }

  static update(
    updatedBranch: {
      companyId: number;
      name: string;
      addressLines: Array<string>;
      locality: string;
      region: string;
      postcode: string;
      countryId: string;
      erpId: string;
    },
    id: number,
  ): Promise<Record<string, unknown>> {
    return axiosInstance.patch(`/api/v1/branches/${id}`, { payload: updatedBranch }).then((res) => ({ ...res.data }));
  }

  static getAddTemplate(locale): Promise<Record<string, unknown>> {
    return axiosInstance.get(`/api/v1/templates/branches/add`, {
      // responseType: 'arraybuffer',
      params: {
        locale: locale,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static async postAddTemplate(file: File): Promise<Record<string, unknown>> {
    const fileString = await convertFileToB64(file);
    return axiosInstance.post(
      `/api/v1/templates/branches/add`,
      { payload: fileString },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  add(): Promise<Record<string, unknown>> {
    return axiosInstance
      .post(`/api/v1/branches`, {
        payload: this,
      })
      .then((res) => ({ ...res.data }));
  }
}
